'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth } from '@morfi/backend-sdk-v2/client';
import { createOnrampClient, type DirectPurchaseCheckout, directPurchaseEntryScope, fundingNavigationManifest, OnrampApiError, type OnrampClient } from '@morfi/onramp-sdk';
import { useConnectedExchanges, useFundingAuthentication, useFundingNavigation, useOnrampSession, usePendingFundingOperation } from '@morfi/onramp-sdk/react';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatedButton } from '@/components/ui/animated-button';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { toSdkApiBase } from '@/lib/network/sdk-api-base';
import { useNetworkConfig } from '@/lib/network/use-network-config';
import { logger } from '@/lib/telemetry/logger';
import { FundingExchange } from './checkout/exchange/funding-exchange';
import { FundingCheckout } from './checkout/funding-checkout';
import { FundingMethodPicker } from './checkout/funding-method-picker';
import { FundingConnectedWallet } from './connected-wallet/funding-connected-wallet';
import { FundingCryptoDeposit } from './crypto-deposit/funding-crypto-deposit';
import { FundingHistory } from './history/funding-history';
import { FundingReceipt } from './history/funding-receipt';
import { FundingManualTransfer } from './manual-transfer/funding-manual-transfer';
import { WithdrawCheckout } from './offramp/withdraw-checkout';
import { FundingChromeContext, useFundingChrome } from './shared/funding-chrome';
import { FundingDialog } from './shared/funding-dialog';
import { FundingLoading } from './shared/funding-loading';
import { useFundingRealtime } from './shared/funding-realtime';
import { fundingTelemetry } from './shared/funding-telemetry';
import { useFundingResource } from './shared/use-funding-resource';
export function FundingEntry(t0) {
  const $ = _c(11);
  const {
    open: t1,
    mode: t2,
    onClose,
    directPurchase
  } = t0;
  const open = t1 === undefined ? true : t1;
  const mode = t2 === undefined ? "deposit" : t2;
  const config = useNetworkConfig();
  const {
    user,
    isAuthenticated
  } = useAuth();
  if (!isAuthenticated || !user) {
    return null;
  }
  const t3 = `${user.id}:${config.apiUrl}:${config.apiKey ?? ""}`;
  let t4;
  if ($[0] !== directPurchase) {
    t4 = directPurchase ? {
      directPurchase
    } : {};
    $[0] = directPurchase;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== config.apiKey || $[3] !== config.apiUrl || $[4] !== mode || $[5] !== onClose || $[6] !== open || $[7] !== t3 || $[8] !== t4 || $[9] !== user.id) {
    t5 = <FundingClient key={t3} open={open} mode={mode} {...t4} owner={user.id} apiUrl={config.apiUrl} apiKey={config.apiKey} onClose={onClose} />;
    $[2] = config.apiKey;
    $[3] = config.apiUrl;
    $[4] = mode;
    $[5] = onClose;
    $[6] = open;
    $[7] = t3;
    $[8] = t4;
    $[9] = user.id;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function FundingClient(t0) {
  const $ = _c(11);
  const {
    open,
    mode,
    owner,
    apiUrl,
    apiKey,
    onClose,
    directPurchase
  } = t0;
  const [profileOpen, setProfileOpen] = useState(false);
  let t1;
  if ($[0] !== apiKey || $[1] !== apiUrl || $[2] !== owner) {
    t1 = () => createOnrampClient({
      telemetry: fundingTelemetry,
      progressKey: `${owner}:${apiUrl}`,
      baseUrl: process.env.NODE_ENV === "development" ? window.location.origin : toSdkApiBase(apiUrl),
      getAuthHeaders: async () => ({
        "X-API-Key": apiKey ?? ""
      })
    });
    $[0] = apiKey;
    $[1] = apiUrl;
    $[2] = owner;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const client = useFundingResource(t1);
  let t2;
  if ($[4] !== client || $[5] !== directPurchase || $[6] !== mode || $[7] !== onClose || $[8] !== open || $[9] !== profileOpen) {
    t2 = client ? mode === "withdraw" && !profileOpen ? <WithdrawCheckout client={client} open={open} onClose={onClose} onProfile={() => setProfileOpen(true)} /> : <FundingEntryBody key={client.cacheKey} client={client} open={open} profileOnly={profileOpen || mode === "profile"} onClose={profileOpen ? () => setProfileOpen(false) : onClose} {...directPurchase ? {
      directPurchase
    } : {}} /> : null;
    $[4] = client;
    $[5] = directPurchase;
    $[6] = mode;
    $[7] = onClose;
    $[8] = open;
    $[9] = profileOpen;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
function FundingEntryBody(props) {
  const $ = _c(15);
  let t0;
  if ($[0] !== props.directPurchase) {
    t0 = props.directPurchase ? {
      ...props.directPurchase.purchase,
      preferredMethod: props.directPurchase.preferredMethod
    } : undefined;
    $[0] = props.directPurchase;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const navigation = useFundingNavigation(props.client, "deposit", t0);
  const visited = useRef(false);
  let t1;
  if ($[2] !== navigation || $[3] !== props.directPurchase || $[4] !== props.open || $[5] !== props.profileOnly) {
    t1 = () => {
      if (props.open) {
        if (props.profileOnly) {
          navigation.deposit();
        } else {
          navigation.restore(visited.current || Boolean(props.directPurchase && fundingNavigationManifest.direct_token_purchase.close === "preserve"));
        }
        visited.current = true;
      }
    };
    $[2] = navigation;
    $[3] = props.directPurchase;
    $[4] = props.open;
    $[5] = props.profileOnly;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== navigation.deposit || $[8] !== navigation.restore || $[9] !== props.directPurchase || $[10] !== props.open || $[11] !== props.profileOnly) {
    t2 = [props.open, props.directPurchase, props.profileOnly, navigation.deposit, navigation.restore];
    $[7] = navigation.deposit;
    $[8] = navigation.restore;
    $[9] = props.directPurchase;
    $[10] = props.open;
    $[11] = props.profileOnly;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  useEffect(t1, t2);
  let t3;
  if ($[13] !== props) {
    t3 = <FundingEntryVisit {...props} />;
    $[13] = props;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
}
function FundingEntryVisit(t0) {
  const $ = _c(107);
  const {
    open,
    client,
    onClose,
    profileOnly: t1,
    directPurchase
  } = t0;
  const profileOnly = t1 === undefined ? false : t1;
  const t = useTranslations("onramp");
  const t2 = profileOnly ? "profile_title" : "deposit_title";
  let t3;
  if ($[0] !== t || $[1] !== t2) {
    t3 = t(t2);
    $[0] = t;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const chrome = useFundingChrome(t3);
  const profileRequested = useRef(false);
  const profileReady = !chrome.picker && chrome.hasBack;
  const profileEntered = useRef(false);
  let t4;
  if ($[3] !== chrome || $[4] !== onClose || $[5] !== profileOnly) {
    t4 = () => {
      if (!profileOnly) {
        return;
      }
      if (!profileRequested.current && chrome.hasProfile) {
        profileRequested.current = true;
        chrome.openProfile();
      } else {
        if (profileRequested.current && !chrome.picker) {
          profileEntered.current = true;
        } else {
          if (profileEntered.current && chrome.picker) {
            onClose();
          }
        }
      }
    };
    $[3] = chrome;
    $[4] = onClose;
    $[5] = profileOnly;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== chrome.hasProfile || $[8] !== chrome.openProfile || $[9] !== chrome.picker || $[10] !== onClose || $[11] !== profileOnly) {
    t5 = [profileOnly, chrome.hasProfile, chrome.picker, chrome.openProfile, onClose];
    $[7] = chrome.hasProfile;
    $[8] = chrome.openProfile;
    $[9] = chrome.picker;
    $[10] = onClose;
    $[11] = profileOnly;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useEffect(t4, t5);
  const [checkoutContainer, setCheckoutContainer] = useState(null);
  const [sessionGeneration, setSessionGeneration] = useState(0);
  let t6;
  let t7;
  if ($[13] !== chrome.picker || $[14] !== profileOnly) {
    t6 = () => {
      if (profileOnly && !profileRequested.current && !chrome.picker) {
        logger.warn("onramp.profile_entry_unavailable", {
          stage: "profile_entry"
        });
      }
    };
    t7 = [profileOnly, chrome.picker];
    $[13] = chrome.picker;
    $[14] = profileOnly;
    $[15] = t6;
    $[16] = t7;
  } else {
    t6 = $[15];
    t7 = $[16];
  }
  useEffect(t6, t7);
  const [manualExchangeOpen, setManualExchangeOpen] = useState(false);
  const [dismissedExchangeId, setDismissedExchangeId] = useState(null);
  const [dismissedCryptoDepositId, setDismissedCryptoDepositId] = useState(null);
  const [selectedCryptoDepositId, setSelectedCryptoDepositId] = useState(null);
  const [dismissedConnectedWalletId, setDismissedConnectedWalletId] = useState(null);
  const [selectedConnectedWalletId, setSelectedConnectedWalletId] = useState(null);
  const exchanges = useConnectedExchanges(client, "web");
  const realtime = useFundingRealtime(client);
  let t8;
  if ($[17] !== directPurchase) {
    t8 = directPurchase ? {
      ...directPurchase.purchase,
      preferredMethod: directPurchase.preferredMethod
    } : undefined;
    $[17] = directPurchase;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const navigation = useFundingNavigation(client, "deposit", t8);
  const [exchangeRevision, setExchangeRevision] = useState(navigation.revision);
  const historyOpen = navigation.page === "history" || navigation.page === "receipt" && navigation.receiptOrigin === "history";
  const receiptId = navigation.page === "receipt" && navigation.receiptOrigin === "deposit" ? navigation.receiptId : null;
  const t9 = navigation.page === "deposit";
  let t10;
  if ($[19] !== sessionGeneration || $[20] !== t9) {
    t10 = {
      enabled: t9,
      navigationKey: sessionGeneration
    };
    $[19] = sessionGeneration;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const session = useOnrampSession(client, t10);
  const pending = usePendingFundingOperation(client, navigation.page === "deposit", navigation.revision);
  let t11;
  let t12;
  if ($[22] !== pending.error) {
    t11 = () => {
      if (pending.error) {
        logger.warn("onramp.pending_recovery_failed");
      }
    };
    t12 = [pending.error];
    $[22] = pending.error;
    $[23] = t11;
    $[24] = t12;
  } else {
    t11 = $[23];
    t12 = $[24];
  }
  useEffect(t11, t12);
  const pendingExchangeId = pending.data?.method === "cex_connect" ? pending.data.id : null;
  const pendingCryptoDepositId = pending.data?.method === "crypto_deposit" ? pending.data.id : null;
  const pendingConnectedWalletId = pending.data?.method === "wallet_connect" ? pending.data.id : null;
  const activeCryptoDepositId = selectedCryptoDepositId ?? pendingCryptoDepositId;
  const cryptoDepositOpen = navigation.page === "crypto_deposit" || navigation.page === "deposit" && Boolean(activeCryptoDepositId && activeCryptoDepositId !== dismissedCryptoDepositId);
  const activeConnectedWalletId = selectedConnectedWalletId ?? pendingConnectedWalletId;
  const connectedWalletOpen = navigation.page === "connected_wallet" || navigation.page === "deposit" && Boolean(activeConnectedWalletId && activeConnectedWalletId !== dismissedConnectedWalletId);
  const exchangeOpen = navigation.page === "deposit" && navigation.revision === exchangeRevision && (manualExchangeOpen || Boolean(pendingExchangeId && pendingExchangeId !== dismissedExchangeId));
  let t13;
  if ($[25] !== navigation.revision || $[26] !== pendingExchangeId) {
    t13 = function setExchangeOpen(openExchange) {
      if (openExchange) {
        setExchangeRevision(navigation.revision);
      }
      setManualExchangeOpen(openExchange);
      if (!openExchange) {
        setDismissedExchangeId(pendingExchangeId);
      }
    };
    $[25] = navigation.revision;
    $[26] = pendingExchangeId;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const setExchangeOpen = t13;
  let t14;
  if ($[28] !== navigation) {
    t14 = () => navigation.transition("session_refreshed");
    $[28] = navigation;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const authentication = useFundingAuthentication(client, t14);
  const currentSession = authentication.session ?? session.data;
  let t15;
  let t16;
  if ($[30] !== authentication.error) {
    t15 = () => {
      if (authentication.error) {
        logger.warn("onramp.auth_session_failed");
      }
    };
    t16 = [authentication.error];
    $[30] = authentication.error;
    $[31] = t15;
    $[32] = t16;
  } else {
    t15 = $[31];
    t16 = $[32];
  }
  useEffect(t15, t16);
  const sessionRejected = session.error instanceof OnrampApiError && session.error.status === 401;
  const entryError = receiptId || currentSession && !sessionRejected ? null : session.error;
  const {
    openLogin
  } = useAuthGate();
  const expired = entryError instanceof OnrampApiError && entryError.status === 401;
  let t17;
  let t18;
  if ($[33] !== entryError) {
    t17 = () => {
      if (entryError) {
        logger.warn("onramp.session_failed", {
          code: entryError instanceof OnrampApiError ? entryError.code : "onramp_request_failed",
          requestId: entryError instanceof OnrampApiError ? entryError.requestId : undefined
        });
      }
    };
    t18 = [entryError];
    $[33] = entryError;
    $[34] = t17;
    $[35] = t18;
  } else {
    t17 = $[34];
    t18 = $[35];
  }
  useEffect(t17, t18);
  let t19;
  if ($[36] !== onClose || $[37] !== openLogin) {
    t19 = function signIn() {
      onClose();
      openLogin();
    };
    $[36] = onClose;
    $[37] = openLogin;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const signIn = t19;
  let t20;
  if ($[39] !== activeConnectedWalletId || $[40] !== activeCryptoDepositId || $[41] !== chrome || $[42] !== client || $[43] !== connectedWalletOpen || $[44] !== cryptoDepositOpen || $[45] !== currentSession || $[46] !== directPurchase || $[47] !== entryError || $[48] !== exchangeOpen || $[49] !== exchanges || $[50] !== expired || $[51] !== navigation || $[52] !== onClose || $[53] !== open || $[54] !== pending.data || $[55] !== pending.isSuccess || $[56] !== pendingExchangeId || $[57] !== profileOnly || $[58] !== profileReady || $[59] !== realtime || $[60] !== receiptId || $[61] !== session || $[62] !== setExchangeOpen || $[63] !== signIn || $[64] !== t) {
    t20 = function renderDialog() {
      return <FundingDialog open={open} onClose={onClose} onHistory={() => navigation.openHistory()} chrome={profileOnly ? {
        ...chrome,
        picker: false,
        title: !profileReady ? t("profile_title") : chrome.title
      } : chrome} direct={navigation.page === "manual_transfer" || cryptoDepositOpen || connectedWalletOpen}>{profileOnly && !profileReady ? chrome.picker ? <FundingLoading /> : <output>{t("profile_load_error")}</output> : null}<div className="rr-block flex flex-col gap-4" hidden={profileOnly && !profileReady}>{connectedWalletOpen ? <FundingConnectedWallet client={client} active={open} operationId={activeConnectedWalletId} onBack={() => {
            setDismissedConnectedWalletId(activeConnectedWalletId);
            setSelectedConnectedWalletId(null);
            navigation.back();
          }} onAddressDeposit={() => {
            setDismissedConnectedWalletId(activeConnectedWalletId);
            setSelectedConnectedWalletId(null);
            navigation.openCryptoDeposit();
          }} onComplete={id => {
            setDismissedConnectedWalletId(id);
            setSelectedConnectedWalletId(null);
            navigation.openReceipt(id, "deposit");
          }} /> : cryptoDepositOpen ? <FundingCryptoDeposit client={client} active={open} operationId={activeCryptoDepositId} onBack={() => {
            setDismissedCryptoDepositId(activeCryptoDepositId);
            setSelectedCryptoDepositId(null);
            navigation.back();
          }} onComplete={id_0 => {
            setDismissedCryptoDepositId(id_0);
            setSelectedCryptoDepositId(null);
            navigation.openReceipt(id_0, "deposit");
          }} /> : navigation.page === "manual_transfer" ? <FundingManualTransfer onBack={navigation.back} /> : exchangeOpen ? <FundingExchange client={client} open={open} operationId={pendingExchangeId} onBack={() => setExchangeOpen(false)} renderStatus={id_1 => <FundingReceipt onReturnToPurchase={onClose} client={client} operationId={id_1} progress={true} onBack={() => setExchangeOpen(false)} onSignIn={signIn} />} /> : !receiptId && (!currentSession || entryError) ? <FundingMethodPicker client={client} methods={[]} loading={!entryError} onExchange={exchanges.data?.includes("coinbase") ? () => setExchangeOpen(true) : undefined} providerFeedback={entryError ? <div role="alert" className="flex flex-col gap-3 rounded-card border border-border-secondary p-4"><p className="text-body">{expired ? t("sign_in") : t("provider_unavailable")}</p>{pending.isSuccess && pending.data === null && <p className="text-caption text-foreground-secondary">{t("no_payment_taken")}</p>}<AnimatedButton variant="secondary" onClick={expired ? signIn : () => {
              session.refetch({
                cancelRefetch: false
              });
            }}>{expired ? t("sign_in") : t("retry")}</AnimatedButton></div> : null} /> : receiptId ? <FundingReceipt onReturnToPurchase={onClose} client={client} operationId={receiptId} {...realtime ? {
            realtime
          } : {}} onBack={directPurchase && fundingNavigationManifest.direct_token_purchase.receipt_back === "origin" ? onClose : navigation.back} onSignIn={signIn} /> : currentSession ? <div ref={setCheckoutContainer} /> : null}</div></FundingDialog>;
    };
    $[39] = activeConnectedWalletId;
    $[40] = activeCryptoDepositId;
    $[41] = chrome;
    $[42] = client;
    $[43] = connectedWalletOpen;
    $[44] = cryptoDepositOpen;
    $[45] = currentSession;
    $[46] = directPurchase;
    $[47] = entryError;
    $[48] = exchangeOpen;
    $[49] = exchanges;
    $[50] = expired;
    $[51] = navigation;
    $[52] = onClose;
    $[53] = open;
    $[54] = pending.data;
    $[55] = pending.isSuccess;
    $[56] = pendingExchangeId;
    $[57] = profileOnly;
    $[58] = profileReady;
    $[59] = realtime;
    $[60] = receiptId;
    $[61] = session;
    $[62] = setExchangeOpen;
    $[63] = signIn;
    $[64] = t;
    $[65] = t20;
  } else {
    t20 = $[65];
  }
  const renderDialog = t20;
  const t21 = chrome.report;
  let t22;
  if ($[66] !== renderDialog) {
    t22 = renderDialog();
    $[66] = renderDialog;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  let t23;
  if ($[68] !== authentication || $[69] !== checkoutContainer || $[70] !== client || $[71] !== connectedWalletOpen || $[72] !== cryptoDepositOpen || $[73] !== currentSession || $[74] !== directPurchase || $[75] !== entryError || $[76] !== exchangeOpen || $[77] !== exchanges || $[78] !== navigation.page || $[79] !== navigation.revision || $[80] !== onClose || $[81] !== open || $[82] !== realtime || $[83] !== receiptId || $[84] !== setExchangeOpen || $[85] !== signIn) {
    t23 = currentSession && !entryError && !exchangeOpen && !cryptoDepositOpen && !connectedWalletOpen ? <FundingCheckout key={`${currentSession.sessionToken}:${directPurchaseEntryScope(directPurchase)}`} {...directPurchase ? {
      directPurchase
    } : {}} checkoutRevision={navigation.revision} open={open} hostActive={navigation.page !== "manual_transfer" && navigation.page !== "connected_wallet"} client={client} {...realtime ? {
      realtime
    } : {}} sessionToken={currentSession.sessionToken} sessionProofId={currentSession.sessionProofId} authenticationRequested={authentication.authenticationRequested} authenticationBusy={authentication.busy} authenticationError={authentication.error} onAcceptAuthenticationTerms={authentication.acceptTerms} onClose={onClose} onSignIn={signIn} onExchange={exchanges.data?.includes("coinbase") ? () => setExchangeOpen(true) : undefined} onSessionRefresh={reason => {
      if (reason === "disconnect") {
        authentication.reset();
        setSessionGeneration(_temp);
      } else {
        authentication.replace();
      }
    }} render={content => navigation.page !== "manual_transfer" && navigation.page !== "connected_wallet" && !receiptId && checkoutContainer ? createPortal(content, checkoutContainer) : null} /> : null;
    $[68] = authentication;
    $[69] = checkoutContainer;
    $[70] = client;
    $[71] = connectedWalletOpen;
    $[72] = cryptoDepositOpen;
    $[73] = currentSession;
    $[74] = directPurchase;
    $[75] = entryError;
    $[76] = exchangeOpen;
    $[77] = exchanges;
    $[78] = navigation.page;
    $[79] = navigation.revision;
    $[80] = onClose;
    $[81] = open;
    $[82] = realtime;
    $[83] = receiptId;
    $[84] = setExchangeOpen;
    $[85] = signIn;
    $[86] = t23;
  } else {
    t23 = $[86];
  }
  let t24;
  if ($[87] !== realtime) {
    t24 = realtime ? {
      realtime
    } : {};
    $[87] = realtime;
    $[88] = t24;
  } else {
    t24 = $[88];
  }
  const t25 = open && historyOpen;
  let t26;
  let t27;
  let t28;
  if ($[89] !== navigation) {
    t26 = id_2 => {
      setDismissedCryptoDepositId(null);
      setSelectedCryptoDepositId(id_2);
      navigation.openCryptoDeposit();
    };
    t27 = id_3 => {
      setDismissedConnectedWalletId(null);
      setSelectedConnectedWalletId(id_3);
      navigation.openConnectedWallet();
    };
    t28 = open_0 => {
      if (open_0) {
        navigation.openHistory();
      } else {
        navigation.closeHistory();
      }
    };
    $[89] = navigation;
    $[90] = t26;
    $[91] = t27;
    $[92] = t28;
  } else {
    t26 = $[90];
    t27 = $[91];
    t28 = $[92];
  }
  let t29;
  if ($[93] !== client || $[94] !== onClose || $[95] !== signIn || $[96] !== t24 || $[97] !== t25 || $[98] !== t26 || $[99] !== t27 || $[100] !== t28) {
    t29 = <FundingChromeContext.Provider value={null}><FundingHistory onReturnToPurchase={onClose} client={client} {...t24} open={t25} onResumeCryptoDeposit={t26} onResumeConnectedWallet={t27} onOpenChange={t28} onSignIn={signIn} /></FundingChromeContext.Provider>;
    $[93] = client;
    $[94] = onClose;
    $[95] = signIn;
    $[96] = t24;
    $[97] = t25;
    $[98] = t26;
    $[99] = t27;
    $[100] = t28;
    $[101] = t29;
  } else {
    t29 = $[101];
  }
  let t30;
  if ($[102] !== chrome.report || $[103] !== t22 || $[104] !== t23 || $[105] !== t29) {
    t30 = <FundingChromeContext.Provider value={t21}>{t22}{t23}{t29}</FundingChromeContext.Provider>;
    $[102] = chrome.report;
    $[103] = t22;
    $[104] = t23;
    $[105] = t29;
    $[106] = t30;
  } else {
    t30 = $[106];
  }
  return t30;
}
function _temp(current) {
  return current + 1;
}